import { useNavigate } from 'react-router-dom';
import { useAuth } from '../useAuth.js';
import { useScope, scopeItems } from '../components/ScopeToggle.jsx';
import ScopeToggle from '../components/ScopeToggle.jsx';
import { usePhoneContactCount } from '../hooks/usePhoneContacts.js';
import { isGroup, memberIds } from '../lib/contactGroups.js';
import { useAudiences } from '../hooks/useAudiences.js';
import { useSends, useDraftActions } from '../hooks/useSend.js';
import { useSettings } from '../hooks/useSettings.js';
import { SENDABLE_TEMPLATE_LIST, getTemplate } from '../templates/index.js';

const STATUS_LABEL = {
  draft: 'Draft', scheduled: 'Scheduled', pending_approval: 'Pending approval',
  pending_review: 'Pending review', sending: 'Sending', paused: 'Paused',
  sent: 'Sent', partial: 'Partial', failed: 'Failed', cancelled: 'Cancelled',
};

export default function Dashboard() {
  const { displayName, email } = useAuth();
  const navigate = useNavigate();
  const contactCount = usePhoneContactCount();
  const { audiences } = useAudiences();
  const { sends } = useSends();
  const { branding, footer, sender } = useSettings();
  const { createDraft } = useDraftActions();

  const [scope, setScope] = useScope('eshot:dashboard-scope', 'mine');
  const myMineSends = scopeItems(sends, 'mine', email);
  const scopedSends = scopeItems(sends, scope, email);
  const scopedAudiences = scopeItems(audiences, scope, email);

  const drafts = scopedSends.filter((s) => s.status === 'draft');
  const sent = scopedSends.filter((s) => ['sent', 'partial'].includes(s.status));
  const recent = scopedSends.slice(0, 5);
  const scopedGroups = scopedAudiences.filter(isGroup);
  const topAudiences = [...scopedGroups].sort((a, b) => memberIds(b).length - memberIds(a).length).slice(0, 4);
  const settingsHealth = computeSettingsHealth({ branding, footer, sender });

  async function newDraft(templateId) {
    const id = await createDraft({ templateId: templateId || null, name: 'Untitled broadcast' });
    navigate(`/sends/${id}`);
  }

  return (
    <div className="space-y-6">
      <Hero displayName={displayName} onNew={() => newDraft()} />

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {scope === 'mine' ? 'Your activity' : 'All activity'}
        </h2>
        <ScopeToggle
          scope={scope}
          onChange={setScope}
          mineCount={myMineSends.length}
          allCount={sends.length}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Contacts" value={contactCount == null ? '…' : contactCount.toLocaleString()} sub="records on the phone, not all listed or emailable" />
        <Stat label="Groups" value={audiences.filter(isGroup).length} sub="to send to" />
        <Stat label="Drafts" value={drafts.length} sub="in progress" />
        <Stat label="Sent" value={sent.length} sub="all time" highlight={sent.length > 0} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 surface-card p-5">
          <header className="flex items-center justify-between mb-3">
            <h2 className="font-medium">Recent sends</h2>
            <button onClick={() => navigate('/sends')} className="text-xs text-primary-600 dark:text-primary-300 hover:underline">View all →</button>
          </header>
          {recent.length === 0 ? (
            <EmptyHint
              title="No sends yet"
              body="Start a draft to see it here. Drafts auto-save while you compose."
              action={{ label: 'Start a draft', onClick: () => newDraft() }}
            />
          ) : (
            <ul className="divide-y divide-gray-200/60 dark:divide-gray-800">
              {recent.map((s) => {
                const tmpl = s.templateId ? getTemplate(s.templateId) : null;
                return (
                  <li
                    key={s.id}
                    onClick={() => navigate(`/sends/${s.id}`)}
                    className="py-3 flex items-center gap-3 cursor-pointer hover:bg-white/40 dark:hover:bg-white/5 -mx-2 px-2 rounded transition"
                  >
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${statusStyle(s.status)}`}>
                      {STATUS_LABEL[s.status] || s.status}
                    </span>
                    <span className="font-medium truncate flex-1">{s.name || 'Untitled'}</span>
                    <span className="text-xs text-gray-500 hidden md:inline">{tmpl?.name || s.templateId || '—'}</span>
                    <span className="text-xs text-gray-500 tabular-nums">{(s.recipientCount || 0).toLocaleString()}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="surface-card p-5">
          <header className="flex items-center justify-between mb-3">
            <h2 className="font-medium">Biggest groups</h2>
            <button onClick={() => navigate('/audiences')} className="text-xs text-primary-600 dark:text-primary-300 hover:underline">Manage →</button>
          </header>
          {topAudiences.length === 0 ? (
            <EmptyHint
              compact
              title="No groups yet"
              body="Tick people on the Contacts page and add them to a group, then send to it."
              action={{ label: 'Find people', onClick: () => navigate('/contacts') }}
            />
          ) : (
            <ul className="space-y-2">
              {topAudiences.map((a) => (
                <li
                  key={a.id}
                  onClick={() => navigate('/audiences')}
                  className="flex items-center justify-between text-sm cursor-pointer hover:bg-white/40 dark:hover:bg-white/5 -mx-2 px-2 py-1.5 rounded transition"
                >
                  <span className="truncate">{a.name}</span>
                  <span className="text-xs text-gray-500 tabular-nums">{memberIds(a).length.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <SettingsHealthCard health={settingsHealth} onJump={() => navigate('/settings')} />

      <section className="surface-card p-5">
        <header className="mb-3">
          <h2 className="font-medium">Quick start a template</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Click to start a draft. Plain text is the only template that sends today; the branded ones follow once their layouts render in the email.
          </p>
        </header>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SENDABLE_TEMPLATE_LIST.map((t) => (
            <button
              key={t.id}
              onClick={() => newDraft(t.id)}
              className="text-left p-4 rounded-lg border border-gray-200/70 dark:border-gray-800 bg-white/60 dark:bg-gray-900/40 hover:border-primary-300 hover:bg-primary-50/30 dark:hover:bg-primary-500/10 transition"
            >
              <p className="font-semibold text-sm">{t.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-3">{t.description}</p>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function Hero({ displayName, onNew }) {
  return (
    <section className="relative overflow-hidden surface-card p-6 sm:p-8">
      <div className="absolute inset-0 opacity-90 pointer-events-none"
        style={{ background: 'linear-gradient(120deg, rgba(0,137,123,0.08) 0%, rgba(45,212,191,0.06) 50%, transparent 100%)' }} />
      <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider font-semibold text-primary-600 dark:text-primary-300">Trovex · Broadcast</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1">Welcome back, {displayName?.split(' ')[0] || 'there'}.</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2 max-w-xl">
            Compose, schedule and personalise broadcast email to Trovex customers. Drafts auto-save as you work.
          </p>
        </div>
        <button onClick={onNew}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-semibold shadow-md shadow-teal-500/25 hover:shadow-teal-500/40 transition whitespace-nowrap">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New broadcast
        </button>
      </div>
    </section>
  );
}

function Stat({ label, value, sub, highlight }) {
  return (
    <div className={`surface-card p-4 ${highlight ? 'ring-2 ring-primary-500/20' : ''}`}>
      <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">{label}</p>
      <p className="text-2xl font-bold mt-1 tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function EmptyHint({ title, body, action, compact }) {
  return (
    <div className={`text-center ${compact ? 'py-3' : 'py-6'}`}>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{body}</p>
      {action && (
        <button onClick={action.onClick} className="text-sm text-primary-600 dark:text-primary-300 hover:underline mt-2">
          {action.label} →
        </button>
      )}
    </div>
  );
}

function SettingsHealthCard({ health, onJump }) {
  return (
    <section className="surface-card p-5">
      <header className="flex items-center justify-between mb-2">
        <h2 className="font-medium">Settings</h2>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          health.complete ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
        }`}>{health.passed} / {health.total} ready</span>
      </header>
      <ul className="space-y-1 text-sm">
        {health.checks.map((c) => (
          <li key={c.id} className="flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full ${c.passed ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            <span className={c.passed ? 'text-gray-600 dark:text-gray-300' : 'font-medium'}>{c.label}</span>
          </li>
        ))}
      </ul>
      {!health.complete && (
        <button onClick={onJump} className="text-sm text-primary-600 dark:text-primary-300 hover:underline mt-3">
          Finish set-up →
        </button>
      )}
    </section>
  );
}

function computeSettingsHealth({ branding, footer, sender }) {
  const checks = [
    { id: 'logo',     label: 'Logo uploaded',         passed: !!branding?.logoUrl },
    { id: 'company',  label: 'Company name set',      passed: !!footer?.companyName },
    { id: 'address',  label: 'Registered address set',passed: !!footer?.address },
    { id: 'compno',   label: 'Company number set',    passed: !!footer?.companyNumber },
    { id: 'sender',   label: 'Sender display name',   passed: !!sender?.displayName },
  ];
  const passed = checks.filter((c) => c.passed).length;
  return { checks, passed, total: checks.length, complete: passed === checks.length };
}

function statusStyle(status) {
  switch (status) {
    case 'draft':            return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    case 'scheduled':        return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300';
    case 'pending_approval':
    case 'pending_review':
    case 'paused':           return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300';
    case 'sending':          return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300';
    case 'sent':             return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300';
    case 'partial':          return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300';
    case 'failed':           return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300';
    case 'cancelled':        return 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500';
    default:                 return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  }
}
